import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AppointmentDto,
  AppointmentStatus,
  CalendarEvent,
  CalendarEventCategory,
  CalendarSource,
  CalendarView,
  UserApplications,
} from '@workspace/data-types';
import {
  AlertSnackBarService,
  CalendarComponent,
  CalendarEventDialogComponent,
  getFrenchHolidaysRange,
} from '@workspace/ng-aura';
import { ApplicationStore, UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { map, tap } from 'rxjs';
import { mapAppointmentToCalendarEvents } from '../../utils/appointment-to-calendar.util';

export type AgendaFilterStatus = 'all' | 'confirmed' | 'proposed';

@Component({
  selector: 'etxe-agenda',
  imports: [CalendarComponent, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './etxe-agenda.component.html',
  host: {
    class: 'flex flex-col flex-1 min-h-0 w-full h-full',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EtxeAgendaComponent {
  private readonly api = inject(ApiService);
  private readonly userStore = inject(UserStore);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly alertSnackbar = inject(AlertSnackBarService);

  readonly isOwner = computed(() => this.applicationStore.name() === UserApplications.OWNER);

  readonly currentFilter = signal<AgendaFilterStatus>('all');
  readonly currentView = signal<CalendarView>('month');
  readonly currentDate = signal<Date>(new Date());
  readonly targetAppointmentId = signal<string | null>(null);

  private openedAppointmentId: string | null = null;

  constructor() {
    this.route.queryParamMap
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((params) => {
          const viewParam = params.get('view') as CalendarView | null;
          if (viewParam && ['month', 'week', 'list'].includes(viewParam)) {
            this.currentView.set(viewParam);
          }

          const dateParam = params.get('date');
          if (dateParam) {
            const parsedDate = new Date(dateParam);
            if (!Number.isNaN(parsedDate.getTime())) {
              this.currentDate.set(parsedDate);
            }
          }

          const filterParam = params.get('filter') as AgendaFilterStatus | null;
          if (filterParam && ['all', 'confirmed', 'proposed'].includes(filterParam)) {
            this.currentFilter.set(filterParam);
          }

          const apptId = params.get('appointmentId') || params.get('appointment') || params.get('id');
          if (apptId) {
            this.targetAppointmentId.set(apptId);
          }
        }),
      )
      .subscribe();

    effect(
      () => {
        const targetId = this.targetAppointmentId();
        if (!targetId || this.openedAppointmentId === targetId) {
          return;
        }

        const appts = this.allAppointments();
        if (appts.length === 0 && this.appointmentsResource.isLoading()) {
          return;
        }

        const events = this.allCalendarEvents();
        const matchedEvent = events.find((e) => {
          const appt = e.metadata as AppointmentDto | undefined;
          const apptId = appt?._id
            ? String(appt._id)
            : (appt as { id?: string })?.id
              ? String((appt as { id?: string }).id)
              : '';
          return (
            e.id === targetId ||
            e.id.startsWith(`${targetId}_slot_`) ||
            (apptId !== '' && apptId === targetId)
          );
        });

        if (matchedEvent) {
          this.openedAppointmentId = targetId;
          const eventDate = new Date(matchedEvent.startAt);
          if (!Number.isNaN(eventDate.getTime())) {
            this.currentDate.set(eventDate);
          }

          this.dialog.open(CalendarEventDialogComponent, {
            data: { event: matchedEvent },
            width: '540px',
            maxWidth: '95vw',
            panelClass: 'aura-calendar-event-modal',
          });
          return;
        }

        // Si le rdv existe dans la liste globale mais est masqué par le filtre actuel, basculer sur 'all'
        const rawAppt = appts.find(
          (a) => String(a._id) === targetId || (a as { id?: string }).id === targetId,
        );
        if (rawAppt && this.currentFilter() !== 'all') {
          this.currentFilter.set('all');
        }
      },
      { allowSignalWrites: true },
    );
  }

  // Sources d'agendas superposables (Visites & Rendez-vous, Jours fériés en France)
  readonly calendarSources = signal<CalendarSource[]>([
    {
      id: 'etxe-appointments',
      name: 'Visites et rendez-vous',
      category: 'primary',
      visible: true,
      isSystem: true,
    },
    {
      id: 'french-holidays',
      name: 'Jours fériés (France)',
      category: 'violet',
      visible: true,
      readonly: true,
    },
  ]);

  // Ressource réactive de chargement des rendez-vous
  readonly appointmentsResource = rxResource({
    stream: () =>
      this.api
        .business()
        .etxe()
        .appointments()
        .getMyAppointments()
        .pipe(map((res) => (Array.isArray(res) ? (res as AppointmentDto[]) : []))),
  });

  readonly allAppointments = computed<AppointmentDto[]>(() => {
    const val = this.appointmentsResource.value();
    return Array.isArray(val) ? (val as AppointmentDto[]) : [];
  });

  readonly confirmedAppointmentsCount = computed<number>(() => {
    return this.allAppointments().filter(
      (a) => a.status === AppointmentStatus.CONFIRMED || (a.status as string) === 'confirmed',
    ).length;
  });

  readonly proposedAppointmentsCount = computed<number>(() => {
    return this.allAppointments().filter(
      (a) =>
        a.status === AppointmentStatus.PROPOSED ||
        (a.status as string) === 'proposed' ||
        (a.status as string) === 'pending' ||
        a.status === AppointmentStatus.RESCHEDULED ||
        (a.status as string) === 'rescheduled',
    ).length;
  });

  readonly filteredAppointments = computed<AppointmentDto[]>(() => {
    const filter = this.currentFilter();
    const appts = this.allAppointments();

    if (filter === 'confirmed') {
      return appts.filter(
        (a) => a.status === AppointmentStatus.CONFIRMED || (a.status as string) === 'confirmed',
      );
    }
    if (filter === 'proposed') {
      return appts.filter(
        (a) =>
          a.status === AppointmentStatus.PROPOSED ||
          (a.status as string) === 'proposed' ||
          (a.status as string) === 'pending' ||
          a.status === AppointmentStatus.RESCHEDULED ||
          (a.status as string) === 'rescheduled',
      );
    }
    return appts;
  });

  readonly calendarEvents = computed<CalendarEvent<AppointmentDto>[]>(() => {
    const currentUserId = this.userStore.current()?._id ? String(this.userStore.current()?._id) : undefined;
    const isOwner = this.isOwner();
    return this.filteredAppointments().flatMap((appt) =>
      mapAppointmentToCalendarEvents(appt, currentUserId, isOwner),
    );
  });

  readonly frenchHolidays = computed<CalendarEvent[]>(() => {
    const currentYear = this.currentDate().getFullYear();
    return getFrenchHolidaysRange(currentYear - 1, currentYear + 2);
  });

  readonly allCalendarEvents = computed<CalendarEvent[]>(() => {
    return [...this.calendarEvents(), ...this.frenchHolidays()];
  });

  onFilterChange(status: AgendaFilterStatus): void {
    this.currentFilter.set(status);
  }

  onToggleSource(sourceId: string): void {
    this.calendarSources.update((sources) =>
      sources.map((s) => (s.id === sourceId ? { ...s, visible: !s.visible } : s)),
    );
  }

  getSourceBadgeClass(category?: CalendarEventCategory): string {
    switch (category) {
      case 'violet':
        return 'bg-violet-9 text-white font-semibold shadow-xs';
      case 'primary':
      default:
        return 'bg-primary-9 text-white font-semibold shadow-xs';
    }
  }

  getSourceDotColor(category?: CalendarEventCategory): string {
    switch (category) {
      case 'violet':
        return 'bg-violet-9';
      case 'primary':
      default:
        return 'bg-primary-9';
    }
  }

  onRefresh(): void {
    this.appointmentsResource.reload();
  }

  onEventClick(event: CalendarEvent): void {
    const appt = event.metadata as AppointmentDto | undefined;
    if (appt?.context?.discussion) {
      // Possible d'ouvrir la discussion associée si souhaité
    }
  }

  goToDiscussion(discussionId: string): void {
    void this.router.navigate(['/messages', discussionId]);
  }
}
